import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
    AppBar,
    Toolbar,
    List,
    Typography,
    Divider,
    IconButton,
    ListItem,
    ListItemIcon,
    ListItemText,
    Button,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import BuildIcon from '@mui/icons-material/Build';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import StarIcon from '@mui/icons-material/Star';
import MessageIcon from '@mui/icons-material/Message';
import HomeIcon from '@mui/icons-material/Home';
import LogoutIcon from '@mui/icons-material/Logout';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { useAuth } from '../../hooks/useAuth';
import {
    AdminLayoutContainer,
    DrawerHeader,
    StyledDrawer,
    MainContent,
} from './AdminLayout.styles';

const drawerWidth = 240;

const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/admin' },
    { text: 'Services', icon: <BuildIcon />, path: '/admin/services' },
    { text: 'Projects', icon: <PhotoLibraryIcon />, path: '/admin/projects' },
    { text: 'Testimonials', icon: <StarIcon />, path: '/admin/testimonials' },
    { text: 'Messages', icon: <MessageIcon />, path: '/admin/messages' },
    { text: 'Logout', icon: <LogoutIcon />, path: '/' },
];

const AdminLayout = ({ children }) => {
    const [open, setOpen] = useState(true);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { logout } = useAuth();
    const navigate = useNavigate();

    // Close drawer on mobile by default
    React.useEffect(() => {
        if (isMobile) {
            setOpen(false);
        } else {
            setOpen(true);
        }
    }, [isMobile]);

    const handleMenuItemClick = (item) => {
        if (item.action === 'logout') {
            logout();
            navigate('/');
        }
    };
    const handleDrawerToggle = () => {
        setOpen(!open);
    };

    const handleViewSite = () => {
        window.open('/', '_blank');
    };

    return (
        <AdminLayoutContainer>
            <AppBar
                position="fixed"
                sx={{
                    zIndex: theme.zIndex.drawer + 1,
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.text.primary,
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                }}
            >
                <Toolbar>
                    <IconButton
                        color="inherit"
                        aria-label="toggle drawer"
                        onClick={handleDrawerToggle}
                        edge="start"
                        sx={{ mr: 2 }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Typography
                        variant="h6"
                        noWrap
                        component="div"
                        sx={{ flexGrow: 1 }}
                    >
                        Done Right Admin
                    </Typography>
                    <Button
                        startIcon={<HomeIcon />}
                        onClick={handleViewSite}
                        sx={{ mr: 2 }}
                        variant="outlined"
                        color="secondary"
                    >
                        View Site
                    </Button>
                </Toolbar>
            </AppBar>
            <StyledDrawer
                variant={isMobile ? 'temporary' : 'persistent'}
                open={open}
                onClose={isMobile ? handleDrawerToggle : undefined}
            >
                <DrawerHeader>
                    <Typography variant="h6" sx={{ flexGrow: 1, ml: 2 }}>
                        Admin Menu
                    </Typography>
                    {!isMobile && (
                        <IconButton onClick={handleDrawerToggle}>
                            <ChevronLeftIcon />
                        </IconButton>
                    )}
                </DrawerHeader>
                <Divider />
                <List>
                    {menuItems.map((item) => (
                        <ListItem
                            button
                            key={item.text}
                            component={item.path ? RouterLink : 'div'}
                            to={item.path}
                            onClick={
                                item.action
                                    ? () => handleMenuItemClick(item)
                                    : undefined
                            }
                            sx={{
                                '&.active': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                },
                            }}
                        >
                            <ListItemIcon>{item.icon}</ListItemIcon>
                            <ListItemText primary={item.text} />
                        </ListItem>
                    ))}
                </List>
            </StyledDrawer>
            <MainContent open={open} drawerwidth={drawerWidth}>
                <DrawerHeader />
                {children}
            </MainContent>
        </AdminLayoutContainer>
    );
};

export default AdminLayout;
